/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { Flex, IconButton, Text, Select, SystemStyleObject, ButtonGroup } from '@chakra-ui/core';
import { MdFirstPage, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdOutlineLastPage } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import isUndefined from 'lodash/isUndefined';
import colors from '../../styles/colors';

export type TablePaginationFooterProps = {
  setCurrentItemsList: any;
  finalListOfItems: any[];
  totalItems?: number;
  showingResultsTranslation?: any | undefined;
  currentPage: number;
  setCurrentPage: (num: number) => void;
  showItemsPerPage?: boolean;
  itemsPerPage: number;
  setItemsPerPage: (num: number) => void;
  sx?: SystemStyleObject | undefined;
  paginationTextStyle?: SystemStyleObject | undefined;
  isDisabled?: boolean;
};

export const TablePaginationFooter = ({
  setCurrentItemsList,
  finalListOfItems,
  totalItems,
  showingResultsTranslation,
  currentPage,
  setCurrentPage,
  showItemsPerPage,
  itemsPerPage,
  setItemsPerPage,
  sx,
  paginationTextStyle,
  isDisabled,
}: TablePaginationFooterProps): JSX.Element => {
  const { t } = useTranslation('requisition');
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = finalListOfItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((totalItems ?? finalListOfItems.length) / itemsPerPage);
  const totalCount = totalItems ?? finalListOfItems.length;

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setCurrentItemsList(currentItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalListOfItems, currentPage, itemsPerPage]);

  const handleItemsPerPage = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setCurrentItemsList(currentItems);
  };

  return (
    <>
      <Flex alignItems="center" w="100%" p={4} background={colors.white} sx={sx}>
        <Flex alignItems="center" w="100%" justifyContent="space-between">
          <Flex alignItems="center">
            {showItemsPerPage && (
              <Flex alignItems="center" mr={4}>
                <Text mr={2}>{t('common:pagination.itemsPerPage')}</Text>
                <Select
                  size="sm"
                  maxW="85px"
                  px={2}
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPage(Number(e.target.value))}
                  isDisabled={isDisabled}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
              </Flex>
            )}
            <Text mr={3} data-testid="pagination-text" sx={paginationTextStyle}>
              {isUndefined(showingResultsTranslation)
                ? t('common:pagination.showingResult', {
                    currentItemsLength:
                      itemsPerPage * currentPage < totalCount
                        ? itemsPerPage * currentPage
                        : /* istanbul ignore next */ totalCount,
                    totalItemsLength: totalCount,
                  })
                : // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                  showingResultsTranslation({
                    currentItemsLength: itemsPerPage * currentPage < totalCount ? itemsPerPage * currentPage : totalCount,
                    totalItemsLength: totalCount,
                  })}
            </Text>
          </Flex>

          <ButtonGroup variant="outline" size="xs" alignItems="center">
            <IconButton
              onClick={() => paginate(1)}
              isDisabled={isDisabled || currentPage <= 1}
              variant="outline"
              colorScheme="blue"
              aria-label="First"
              borderColor="#718096"
              icon={<MdFirstPage />}
              data-testid="first-page-button"
            />
            <IconButton
              onClick={() => paginate(currentPage - 1)}
              isDisabled={isDisabled || currentPage <= 1}
              variant="outline"
              colorScheme="blue"
              aria-label="Prev"
              borderColor="#718096"
              icon={<MdKeyboardArrowLeft />}
              data-testid="prev-page-button"
            />
            <IconButton
              onClick={() => paginate(currentPage + 1)}
              isDisabled={isDisabled || totalCount <= itemsPerPage * currentPage}
              variant="outline"
              colorScheme="blue"
              aria-label="Next"
              borderColor="#718096"
              icon={<MdKeyboardArrowRight />}
              data-testid="next-page-button"
            />
            <IconButton
              onClick={() => paginate(totalPages)}
              isDisabled={isDisabled || currentPage === totalPages}
              variant="outline"
              colorScheme="blue"
              aria-label="Last"
              borderColor="#718096"
              icon={<MdOutlineLastPage />}
              data-testid="last-page-button"
            />
          </ButtonGroup>
        </Flex>
      </Flex>
    </>
  );
};
